// src/lib/triage.js
//
// Fault triage. Maps what the caller can see to the likely causes and an
// urgency band.
//
// NEEDS BROOKE'S SIGN-OFF BEFORE LAUNCH. The mappings below are the common
// causes for each symptom, but they are general refrigeration knowledge rather
// than Brooke's own call-out experience. The bands in particular decide what a
// customer is told about how fast they need someone.
//
// Two rules the content follows:
//
//   Nothing here tells anyone to open a unit or touch refrigerant. Every
//   action is either a check the customer can safely make or a reason to call.
//
//   The urgency band never talks anyone down from calling. Where stock is at
//   risk right now the answer is always to call.
//
// How the three inputs combine:
//
//   Equipment decides how much is riding on it. A cold room full of stock and
//   an ice machine are not the same failure.
//
//   Symptom decides the severity, and its own likely causes.
//
//   Duration cuts both ways, which is the part that is easy to get wrong. An
//   acute fault is most urgent when it has just started - a room that has been
//   warm for weeks has already lost whatever was in it. A degenerative fault
//   is the opposite: weeks of icing or noise means it is progressing, and the
//   longer it has run the more likely it takes the compressor with it.

export const EQUIPMENT = [
  {
    id: "cold-room",
    label: "Cold room or freezer room",
    // 0-2, how much product sits behind this piece of equipment
    stockWeight: 2,
    specifics: {
      "not-cooling": [
        "Door seal or closer failed, letting warm air in continuously",
        "Evaporator iced over and blocking airflow",
      ],
      icing: ["Defrost termination sensor failed", "Door left open on a hot day"],
      water: ["Drain line frozen inside the wall penetration"],
    },
  },
  {
    id: "display",
    label: "Display fridge or prep cooler",
    stockWeight: 2,
    specifics: {
      "not-cooling": [
        "Air curtain disrupted by overstocking above the load line",
        "Condenser filter blocked at the front grille",
      ],
      running: ["Night blind not being used, or ambient in the shop too high"],
    },
  },
  {
    id: "underbar",
    label: "Underbar or make table",
    stockWeight: 1,
    specifics: {
      "not-cooling": [
        "Condenser starved of air in a tight cabinet space",
        "Lid or drawer gaskets worn from constant use",
      ],
      water: ["Condensate tray full because the unit is not level"],
    },
  },
  {
    id: "beverage",
    label: "Beverage cooler or upright freezer",
    stockWeight: 1,
    specifics: {
      "not-cooling": ["Door heater or seal failed", "Unit sited against a wall or heat source"],
      icing: ["Door left ajar by stock pushed too far forward"],
    },
  },
  {
    id: "ice",
    label: "Ice machine",
    stockWeight: 0,
    // An ice machine that is not making ice is usually a water problem, not a
    // refrigeration one, so its causes replace the generic list rather than
    // adding to it.
    replaces: {
      "not-cooling": [
        "Water supply shut off or the inlet valve failed",
        "Scale on the evaporator plate stopping the ice releasing",
        "Harvest cycle not completing",
        "Water filter blocked and starving the machine",
      ],
      water: [
        "Drain blocked or not falling away from the machine",
        "Overflow from a stuck float or inlet valve",
      ],
    },
  },
];

// kind: "acute" is a fault that threatens product now. "degenerative" is one
// that gets worse the longer it runs.
export const SYMPTOMS = [
  {
    id: "not-cooling",
    label: "Not cooling",
    blurb: "Temperature climbing or will not pull down",
    kind: "acute",
    severity: 3,
    causes: [
      "Loss of refrigerant charge, usually from a leak",
      "Condenser blocked with dust or grease",
      "Failed compressor or start components",
      "Faulty thermostat or controller",
    ],
    checks: [
      "Check the unit still has power and the door is sealing",
      "Look for a condenser coil packed with dust",
      "Move stock to another unit if you have one",
    ],
  },
  {
    id: "electrical",
    label: "Tripping or not starting",
    blurb: "Breaker trips, or the unit is dead",
    kind: "acute",
    severity: 3,
    causes: [
      "Failed start capacitor or relay",
      "Compressor drawing over current",
      "Moisture in an electrical connection",
      "Supply fault upstream of the unit",
    ],
    checks: [
      "Do not keep resetting a breaker that trips again",
      "Check whether other equipment on the same circuit is affected",
      "Move stock if the unit will not restart",
    ],
  },
  {
    id: "running",
    label: "Running constantly",
    blurb: "Never cycles off, or short cycles",
    kind: "degenerative",
    severity: 2,
    causes: [
      "Low refrigerant charge",
      "Condenser dirty or poorly ventilated",
      "Door seal or hinge worn",
      "Controller set wrong or sensor drifting",
    ],
    checks: [
      "Check nothing is stacked against the condenser",
      "Check the set point has not been changed",
    ],
  },
  {
    id: "icing",
    label: "Icing up",
    blurb: "Ice on the evaporator, panels or product",
    kind: "degenerative",
    severity: 1,
    causes: [
      "Defrost cycle not running or defrost heater failed",
      "Door seal letting warm air in",
      "Blocked drain line backing up",
      "Evaporator fan failure",
    ],
    checks: [
      "Check the door seal closes cleanly along its whole length",
      "Note whether the ice builds in one place or throughout",
    ],
  },
  {
    id: "noise",
    label: "Unusual noise",
    blurb: "Rattling, grinding or knocking",
    kind: "degenerative",
    severity: 1,
    causes: [
      "Failing fan motor or bearing",
      "Loose panel or fan guard",
      "Compressor mounting worn",
      "Liquid returning to the compressor",
    ],
    checks: [
      "Note whether the noise is constant or only on start-up",
      "Note whether it comes from the plant or the room",
    ],
  },
  {
    id: "water",
    label: "Water on the floor",
    blurb: "Pooling under or around the unit",
    kind: "degenerative",
    severity: 1,
    causes: [
      "Blocked or frozen drain line",
      "Condensate tray cracked or overflowing",
      "Door seal allowing condensation",
      "Drain line fallen out of position",
    ],
    checks: [
      "Keep the area dry, it is a slip risk",
      "Note whether the water is clean or oily",
    ],
  },
];

// Two weights per duration, picked by the symptom's kind.
export const DURATIONS = [
  { id: "now", label: "Just now", acute: 2, degenerative: 0 },
  { id: "today", label: "Today", acute: 2, degenerative: 0 },
  { id: "days", label: "A few days", acute: 1, degenerative: 1 },
  { id: "weeks", label: "Weeks or more", acute: 0, degenerative: 2 },
];

const BANDS = {
  critical: {
    key: "critical",
    label: "Call now",
    tone: "critical",
    blurb:
      "Stock is at risk. Call the office rather than waiting on an email - a warm room is measured in hours, not days.",
  },
  high: {
    key: "high",
    label: "Same day",
    tone: "high",
    blurb:
      "Worth getting someone out today. Left alone this usually gets worse and takes the unit with it.",
  },
  medium: {
    key: "medium",
    label: "This week",
    tone: "medium",
    blurb:
      "Not an emergency, but it is a fault rather than a quirk. Booking it in stops it becoming one.",
  },
};

// A line explaining why the band came out where it did, so the result does not
// read as a black box.
function reason(eq, sy, du) {
  if (sy.kind === "acute" && du.acute >= 2) {
    return eq.stockWeight >= 2
      ? `A ${eq.label.toLowerCase()} that has just stopped holding is the case where hours matter.`
      : "It has only just started, which is when there is still something to save.";
  }
  if (sy.kind === "acute") {
    return "It has been going a while, so the immediate risk has passed - but the fault has not.";
  }
  if (du.degenerative >= 2) {
    return "Weeks of this means it is progressing, and it usually takes the compressor with it eventually.";
  }
  return "Caught early, which is the cheap end of this fault.";
}

export function triage({ equipment, symptom, duration }) {
  const eq = EQUIPMENT.find((e) => e.id === equipment) || EQUIPMENT[0];
  const sy = SYMPTOMS.find((s) => s.id === symptom) || SYMPTOMS[0];
  const du = DURATIONS.find((d) => d.id === duration) || DURATIONS[0];

  const durationWeight = sy.kind === "acute" ? du.acute : du.degenerative;
  const score = sy.severity + eq.stockWeight + durationWeight;

  const band = score >= 7 ? BANDS.critical : score >= 4 ? BANDS.high : BANDS.medium;

  // Equipment either replaces the generic causes, adds its own in front of
  // them, or leaves them alone.
  const replaced = eq.replaces && eq.replaces[sy.id];
  const added = eq.specifics && eq.specifics[sy.id];
  const causes = replaced || (added ? [...added, ...sy.causes] : sy.causes);

  return {
    equipment: eq,
    symptom: sy,
    duration: du,
    band,
    score,
    causes,
    specificCount: replaced ? causes.length : added ? added.length : 0,
    reason: reason(eq, sy, du),
  };
}

// Plain text sent with the enquiry, so the office gets a report rather than
// "my fridge is broken".
export function toReport(r) {
  return [
    "Fault report from the website",
    `Equipment: ${r.equipment.label}`,
    `Symptom: ${r.symptom.label} - ${r.symptom.blurb.toLowerCase()}`,
    `Started: ${r.duration.label.toLowerCase()}`,
    `Indicated urgency: ${r.band.label}`,
    "",
    "Possible causes suggested on the site:",
    ...r.causes.map((c) => `  - ${c}`),
    "",
    "Indicative only. The fault is confirmed on site.",
  ].join("\n");
}